import actions from './actions';

function deepCopySelections(selections) {
    return selections.map(s => ({
        ...s,
        items: s.items.map(si => ({...si})),
    }));
}

const initState = {
    isItemModalVisible: false,
    isTransactionModalVisible: false,

    originType: null, // e.g. Type.sale_quote
    transactionType: null, // e.g. Type.sale_invoice

    // What the user selected
    // DATA STRUCTURE:
    // An array of {
    //      transactionId: int,
    //      items: An array of {
    //          id: int,
    //          applyQty: int,
    //      }
    // }
    selections: [],

    // Store all transaction items
    lists: {
        itemModal: [],
        originalQuantities: [], // { transactionId, transactionItemId, quantity } for view.js
    },

    readState: {
        itemModal: null,
        transactionModal: null,
    },

    // initItems: [], // Store all initial form_items (from panelItems)
    //
    // readState: false,
    // readCount: 0,
    // options: [],
    //
    // selectedIds: [], // Ids of all transaction selected
    // allData: [],
    // An array of {
    //  transaction_id: 2,
    //  items: an array of { id: 2, apply_quantity: 30, selected: true, ### id and key optional }}
};

export default function appReducer(state = initState, action = {}) {
    switch (action.type) {
        case actions.TXN_TRANSFER_SET: {
            return {
                ...state,
                ...action.data,
            };
        }
        case actions.TXN_TRANSFER_STORE_ORIGINAL_QUANTITIES:
            return {
                ...state,
                lists: {
                    ...state.lists,
                    originalQuantities: action.originalQuantities,
                },
            };
        case actions.TXN_TRANSFER_RESET:
            return {...initState};
        // ITEM MODAL:
        case actions.TXN_TRANSFER_OPEN_ITEM_MODAL:
            return { ...state, isItemModalVisible: true };
        case actions.TXN_TRANSFER_CLOSE_ITEM_MODAL:
            return { ...state, isItemModalVisible: false };
        case actions.TXN_TRANSFER_CHANGE_QTY: {
            const {
                transactionId,
                transactionItemId,
            } = action;

            const selections = deepCopySelections(state.selections);

            const tIdx = selections.findIndex(s => s.transactionId === transactionId);
            const tiIdx = selections[tIdx].items.findIndex(i => i.id === transactionItemId);

            // Fetch info from list
            const transaction = state.lists.itemModal.find(i => i.id === transactionId);
            const transactionItem = transaction.form_items.find(i => i.id === transactionItemId);

            const range = {
                max:
                    (transactionItem.balance_quantity || 0) + (transactionItem.original_quantity || 0),
                min:
                    (transactionItem.transferred_to_quantity || 0) - (transactionItem.original_quantity || 0),
            };

            let applyQty = parseFloat(action.quantity) || null;
            if (applyQty > range.max)
                applyQty = range.max;
            if (applyQty < range.min)
                applyQty = range.min;

            if (tiIdx >= 0) {
                selections[tIdx].items[tiIdx].applyQty = applyQty;
            }
            else {
                selections[tIdx].items.push({
                    id: transactionItemId,
                    applyQty,
                });
            }
            return { ...state, selections };
        }
        case actions.TXN_TRANSFER_CHANGE_SELECTED_ITEM_IDS: {
            const {
                transactionId,
                selectedItemIds: providedSelectedItemIds,
            } = action;

            const selections = deepCopySelections(state.selections);

            const tIdx = selections.findIndex(s => s.transactionId === transactionId);
            const transaction = state.lists.itemModal.find(i => i.id === transactionId);

            // The old selected item ids
            const oldSelectedItemIds = selections[tIdx].items
                .filter(i => !(i.applyQty === null || i.applyQty === 0))
                .map(i => i.id);

            // Filter out bundle_item ids
            const selectedItemIds = providedSelectedItemIds.filter(id => (
                transaction.form_items.find(i => i.id === id)
            ));

            const toAddIds = selectedItemIds.filter(id => !oldSelectedItemIds.find(oldId => oldId === id));
            const toRemoveIds = oldSelectedItemIds.filter(id => !selectedItemIds.find(newId => newId === id));

            if (toAddIds) {
                toAddIds.forEach(transactionItemId => {
                    const tiIdx = selections[tIdx].items.findIndex(i => i.id === transactionItemId);

                    // Fetch info from list
                    const transactionItem = transaction.form_items.find(i => i.id === transactionItemId);

                    const newApplyQty = (
                        transactionItem.balance_quantity + (transactionItem.original_quantity || 0)
                    );

                    if (tiIdx >= 0) {
                        selections[tIdx].items[tiIdx].applyQty = newApplyQty;
                    }
                    else {
                        selections[tIdx].items.push({
                            id: transactionItemId,
                            applyQty: newApplyQty,
                        });
                    }
                });
            }

            if (toRemoveIds) {
                toRemoveIds.forEach(transactionItemId => {
                    const tiIdx = selections[tIdx].items.findIndex(i => i.id === transactionItemId);
                    selections[tIdx].items[tiIdx].applyQty = null;
                });
            }

            return { ...state, selections };
        }
        case actions.TXN_TRANSFER_REMOVE_TRANSACTION_ID: {
            const {
                transactionId,
            } = action;

            const selections = deepCopySelections(state.selections);
            const tIdx = selections.findIndex(s => s.transactionId === transactionId);

            selections.splice(tIdx, 1);

            return { ...state, selections };
        }

        case actions.TXN_TRANSFER_GET_ITEM_MODAL_DATA_REQUEST:
            return {
                ...state,
                readState: { ...state.readState, itemModal: 'request' },
            };
        case actions.TXN_TRANSFER_GET_ITEM_MODAL_DATA_SUCCESS:
            return {
                ...state,
                readState: { ...state.readState, itemModal: 'success' },
                lists: { ...state.lists, itemModal: action.list },
            };
        case actions.TXN_TRANSFER_GET_ITEM_MODAL_DATA_ERROR:
            return {
                ...state,
                readState: { ...state.readState, itemModal: 'error' },
            };

        // TXN MODAL:
        case actions.TXN_TRANSFER_OPEN_TXN_MODAL:
            return { ...state, isTransactionModalVisible: true };
        case actions.TXN_TRANSFER_CLOSE_TXN_MODAL:
            return { ...state, isTransactionModalVisible: false };
        case actions.TXN_TRANSFER_CHANGE_SELECTED_TRANSACTION_IDS: {
            const {
                oldIds,
                newIds,
            } = action;

            const selections = deepCopySelections(state.selections);

            const toAddIds = newIds.filter(id => !oldIds.find(oldId => oldId === id));
            const toRemoveIds = oldIds.filter(id => !newIds.find(newId => newId === id));

            if (toAddIds) {
                toAddIds.forEach(transactionId => {
                    selections.push({
                        transactionId,
                        items: [],
                    });
                });
            }

            if (toRemoveIds) {
                toRemoveIds.forEach(transactionId => {
                    const tIdx = selections.findIndex(s => s.transactionId === transactionId);
                    selections.splice(tIdx, 1);
                });
            }

            return { ...state, selections };
        }

        default:
            return state;
    }
}
